import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  AuthError,
  AuthErrorCodes
} from 'firebase/auth';
import { auth } from './config';
import { doc, updateDoc, FirestoreError } from 'firebase/firestore';
import { db } from './config';
import { User, OnboardingStatus } from '../../types/user.types';
import { getUserByAuthUid } from './userHelpers';

// PERFORMANCE: Helper to get user-friendly error messages from Firebase Auth errors
const getAuthErrorMessage = (error: AuthError): string => {
  switch ((error instanceof Error ? (error as NodeJS.ErrnoException).code : undefined)) {
    case AuthErrorCodes.INVALID_EMAIL:
      return 'Invalid email address format';
    case AuthErrorCodes.USER_DISABLED:
      return 'This account has been disabled';
    case AuthErrorCodes.USER_DELETED:
      return 'No account found with this email';
    case AuthErrorCodes.INVALID_PASSWORD:
    case AuthErrorCodes.INVALID_LOGIN_CREDENTIALS:
      return 'Invalid email or password';
    case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
      return 'Too many failed attempts. Please try again later';
    case AuthErrorCodes.NETWORK_REQUEST_FAILED:
      return 'Network error. Please check your connection';
    case AuthErrorCodes.EMAIL_EXISTS:
      return 'An account with this email already exists';
    case AuthErrorCodes.WEAK_PASSWORD:
      return 'Password is too weak. Please use a stronger password';
    default:
      return (error instanceof Error ? error.message : 'An authentication error occurred');
  }
};
// Biometric cleanup handled by WebAuthn implementation

export const loginUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // Force token refresh to ensure Firestore recognizes the auth state immediately
    // This fixes a race condition where signIn succeeds but token isn't ready yet
    await userCredential.user.getIdToken(true);

    const userData = await getUserByAuthUid(userCredential.user.uid);
    
    if (!userData) {
      throw new Error('User profile not found');
    }
    
    if (userData.onboardingStatus !== OnboardingStatus.COMPLETE) {
      throw new Error('Please complete your onboarding process before logging in');
    }
    
    // Check BOTH isActive and status fields for backward compatibility
    // User is inactive if EITHER:
    // - isActive is explicitly false (boolean field)
    // - status is 'inactive' (string field, used by UserManagement UI)
    const isUserInactive = userData.isActive === false || userData.status === 'inactive';
    if (isUserInactive) {
      throw new Error('Your account has been deactivated. Please contact an administrator');
    }
    
    return userCredential.user;
  } catch (error) {
    // Type guard for Firebase Auth errors
    if (error && typeof error === 'object' && 'code' in error) {
      throw new Error(getAuthErrorMessage(error as AuthError));
    }
    throw new Error('Login failed');
  }
};

export const logoutUser = async () => {
  try {
    // WebAuthn credentials are managed separately and persist across sessions
    // No need to clear them on logout

    await signOut(auth);
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw new Error(getAuthErrorMessage(error as AuthError));
    }
    throw new Error('Logout failed');
  }
};

export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw new Error(getAuthErrorMessage(error as AuthError));
    }
    throw new Error('Password reset failed');
  }
};

export const createUserWithInvite = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    await updateProfile(userCredential.user, {
      displayName: `${firstName} ${lastName}`
    });
    
    await sendEmailVerification(userCredential.user);
    
    return userCredential.user;
  } catch (error) {
    if (error && typeof error === 'object' && 'code' in error) {
      throw new Error(getAuthErrorMessage(error as AuthError));
    }
    throw new Error('User creation failed');
  }
};

export const updateUserOnboardingStatus = async (
  userId: string,
  status: OnboardingStatus,
  additionalData?: Partial<User>
) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      onboardingStatus: status,
      ...additionalData,
      updatedAt: new Date()
    });
  } catch (error) {
    // Handle Firestore errors
    if (error instanceof FirestoreError) {
      throw new Error(`Failed to update onboarding status: ${(error instanceof Error ? error.message : String(error))}`);
    }
    throw new Error('Failed to update onboarding status');
  }
};

export const getCurrentUser = (): Promise<FirebaseUser | null> => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const subscribeToAuthChanges = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export const checkIfAuthAccountExists = async (email: string): Promise<boolean> => {
  try {
    const signInMethods = await fetchSignInMethodsForEmail(auth, email);
    return signInMethods.length > 0;
  } catch (error) {
    console.error('Error checking auth account:', error);
    return false;
  }
};

export const comparePassword = async (email: string, password: string): Promise<boolean> => {
  try {
    // Try to sign in with the provided credentials
    await signInWithEmailAndPassword(auth, email, password);
    // If successful, password is correct
    return true;
  } catch (error) {
    // If sign in fails, password is incorrect
    // Log error code for debugging if available
    if (error && typeof error === 'object' && 'code' in error) {
      console.error('Password verification failed:', (error as AuthError).code);
    } else {
      console.error('Password verification failed');
    }
    return false;
  }
};